import { supabase } from '../../lib/supabase';
import { Contribution, ContributionRequest } from '../../types/crowdfunding';
import { campaignService } from './campaignService';

export const contributionService = {
  /**
   * Récupère toutes les contributions pour une campagne avec pagination
   */
  async getContributions(campaignId: string, options?: { page?: number, pageSize?: number }) {
    let query = supabase
      .from('contributions')
      .select('*', { count: 'exact' })
      .eq('campaign_id', campaignId);
      
    // Pagination
    if (options?.page !== undefined && options?.pageSize) {
      const from = options.page * options.pageSize;
      const to = from + options.pageSize - 1;
      query = query.range(from, to);
    }
    
    const { data, error, count } = await query.order('created_at', { ascending: false });

    if (error) {
      console.error(`Erreur lors de la récupération des contributions pour la campagne ${campaignId}:`, error);
      throw new Error(`Erreur lors de la récupération des contributions: ${error.message}`);
    }

    return { 
      contributions: data as Contribution[],
      totalCount: count || 0
    };
  },

  /**
   * Récupère toutes les contributions pour une campagne par son slug avec pagination
   */
  async getContributionsBySlug(slug: string, options?: { page?: number, pageSize?: number }) {
    try {
      // D'abord, récupérer l'ID de la campagne à partir du slug
      const campaign = await campaignService.getCampaignBySlug(slug);
      
      if (!campaign) {
        throw new Error(`Aucune campagne trouvée avec le slug ${slug}`);
      }
      
      // Ensuite, utiliser l'ID pour récupérer les contributions
      return await this.getContributions(campaign.id, options);
    } catch (error) {
      console.error(`Erreur lors de la récupération des contributions pour la campagne avec le slug ${slug}:`, error);
      throw new Error(`Erreur lors de la récupération des contributions: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  },

  /**
   * Crée une nouvelle contribution
   */
  async createContribution(contribution: ContributionRequest) {
    // Vérifier si la campagne existe et est active
    const { data: campaign, error: campaignError } = await supabase
      .from('crowdfunding_campaigns')
      .select('id, status, end_date')
      .eq('id', contribution.campaign_id)
      .single();
      
    if (campaignError || !campaign) {
      throw new Error('La campagne spécifiée n\'existe pas');
    }
    
    if (campaign.status !== 'active') {
      throw new Error('Impossible de contribuer à une campagne qui n\'est pas active');
    }
    
    // Vérifier que la campagne n'est pas terminée
    const endDate = new Date(campaign.end_date);
    const now = new Date();
    if (endDate < now) {
      throw new Error('Cette campagne est terminée, les contributions ne sont plus acceptées');
    }
    
    // Valider le montant
    if (contribution.amount < 50) {
      throw new Error('Le montant minimum de contribution est de 50 FCFA');
    }

    // Créer la contribution avec le statut "pending"
    const { data, error } = await supabase
      .from('contributions')
      .insert({
        campaign_id: contribution.campaign_id,
        user_id: contribution.user_id || null,
        amount: contribution.amount,
        payment_status: 'pending',
        contributor_name: contribution.contributor_name,
        contributor_email: contribution.contributor_email,
        contributor_phone: contribution.contributor_phone,
        message: contribution.message,
        is_anonymous: contribution.is_anonymous || false
      })
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de la création de la contribution:', error);
      throw new Error(`Erreur lors de la création de la contribution: ${error.message}`);
    }

    return data as Contribution;
  },

  /**
   * Met à jour le statut de paiement d'une contribution
   */
  async updatePaymentStatus(id: string, status: 'pending' | 'paid' | 'failed') {
    // Vérifier si la contribution existe
    const { data: existingContribution, error: fetchError } = await supabase
      .from('contributions')
      .select('id, campaign_id, payment_status, amount')
      .eq('id', id)
      .single();
      
    if (fetchError || !existingContribution) {
      throw new Error('La contribution spécifiée n\'existe pas');
    }
    
    // Éviter les mises à jour inutiles
    if (existingContribution.payment_status === status) {
      return existingContribution as Contribution;
    }

    // Mettre à jour directement le statut de paiement dans la table contributions
    const { data: updatedContribution, error: updateError } = await supabase
      .from('contributions')
      .update({
        payment_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (updateError) {
      console.error(`Erreur lors de la mise à jour du statut de paiement pour la contribution ${id}:`, updateError);
      throw new Error(`Erreur lors de la mise à jour du statut de paiement: ${updateError.message}`);
    }

    // Si le paiement est réussi, mettre à jour le montant collecté de la campagne
    if (status === 'paid') {
      await this.updateCampaignAmount(existingContribution.campaign_id);
    }

    return updatedContribution as Contribution;
  },

  /**
   * Met à jour le montant total collecté pour une campagne
   */
  async updateCampaignAmount(campaignId: string) {
    // Récupérer toutes les contributions payées pour cette campagne
    const { data, error } = await supabase
      .from('contributions')
      .select('amount')
      .eq('campaign_id', campaignId)
      .eq('payment_status', 'paid');

    if (error) {
      console.error(`Erreur lors de la récupération des contributions pour la campagne ${campaignId}:`, error);
      throw new Error(`Erreur lors de la récupération des contributions: ${error.message}`);
    }

    // Calculer le montant total
    const totalAmount = data.reduce((sum, contribution) => sum + contribution.amount, 0);

    // Mettre à jour le montant collecté de la campagne
    await campaignService.updateCollectedAmount(campaignId, totalAmount);

    return totalAmount;
  },

  /**
   * Récupère les statistiques des contributions pour une campagne
   */
  async getCampaignStats(campaignId: string) {
    // Vérifier si la campagne existe
    const { data: campaign, error: campaignError } = await supabase
      .from('crowdfunding_campaigns')
      .select('id')
      .eq('id', campaignId)
      .single();
      
    if (campaignError || !campaign) {
      throw new Error('La campagne spécifiée n\'existe pas');
    }

    const { data, error } = await supabase
      .from('contributions')
      .select('payment_status, amount, user_id, contributor_email')
      .eq('campaign_id', campaignId);

    if (error) {
      console.error(`Erreur lors de la récupération des statistiques pour la campagne ${campaignId}:`, error);
      throw new Error(`Erreur lors de la récupération des statistiques: ${error.message}`);
    }

    // Calculer les statistiques
    const uniqueContributors = new Set();
    data.forEach(c => {
      if (c.user_id) uniqueContributors.add(c.user_id);
      else if (c.contributor_email) uniqueContributors.add(c.contributor_email);
    });

    const stats = {
      total_contributions: data.length,
      total_amount: 0,
      paid_amount: 0,
      pending_amount: 0,
      failed_amount: 0,
      contributors_count: uniqueContributors.size,
      average_contribution: 0
    };

    data.forEach(contribution => {
      stats.total_amount += contribution.amount;
      
      if (contribution.payment_status === 'paid') {
        stats.paid_amount += contribution.amount;
      } else if (contribution.payment_status === 'pending') {
        stats.pending_amount += contribution.amount;
      } else if (contribution.payment_status === 'failed') {
        stats.failed_amount += contribution.amount;
      }
    });

    // Calculer la contribution moyenne
    if (data.length > 0) {
      stats.average_contribution = Math.round(stats.paid_amount / data.filter(c => c.payment_status === 'paid').length);
    }

    return stats;
  },
  
  /**
   * Récupère les dernières contributions sur toutes les campagnes
   */
  async getRecentContributions(limit = 10, tenantId?: string) {
    let query = supabase
      .from('contributions')
      .select(`
        *,
        campaign:crowdfunding_campaigns (
          id,
          title,
          contestant_id,
          tenant_id,
          contestant:contestants (
            name,
            photo_url
          )
        )
      `)
      .eq('payment_status', 'paid');
      
    // Filtrer par tenant_id si fourni
    if (tenantId) {
      query = query.eq('campaign.tenant_id', tenantId);
    }
    
    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erreur lors de la récupération des contributions récentes:', error);
      throw new Error(`Erreur lors de la récupération des contributions récentes: ${error.message}`);
    }

    return data;
  },
  
  /**
   * Exporte les contributions d'une campagne au format CSV
   */
  async exportContributionsToCSV(campaignId: string) {
    const { data, error } = await supabase
      .from('contributions')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error(`Erreur lors de l'exportation des contributions:`, error);
      throw new Error(`Erreur lors de l'exportation des contributions: ${error.message}`);
    }

    // Définir les en-têtes CSV
    const headers = [
      'ID', 'Nom', 'Email', 'Téléphone', 'Montant', 
      'Statut', 'Message', 'Anonyme', 'Date'
    ];
    
    // Formater les données
    const rows = data.map(c => [
      c.id,
      c.contributor_name || 'Anonyme',
      c.contributor_email || '-',
      c.contributor_phone || '-',
      c.amount,
      c.payment_status === 'paid' ? 'Payé' : c.payment_status === 'pending' ? 'En attente' : 'Échoué',
      c.message || '-',
      c.is_anonymous ? 'Oui' : 'Non',
      new Date(c.created_at).toLocaleString('fr-FR')
    ]);
    
    // Générer le contenu CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  }
};
